/**
 * UsageCreditsPage — the CLIENT-facing usage dashboard.
 *
 * Separate from the operator "Usage & Cost" page (/usage, elevatedAndAgileco):
 * that shows raw provider dollar cost; this shows only credits, so a client
 * sees what they've used without seeing what it cost the agency. Ungated in the
 * nav = visible to clients.
 *
 * Shape follows how mature platforms (OpenAI, Cursor, Vercel, the shadcn
 * credits block) present usage: a headline "how much" number, a spend-over-time
 * chart, and a recent-activity list. Credits count all runs (failures
 * included, ratified); the activity list HIDES failures by default and lets the
 * client filter them in — calm by default, transparent on demand.
 *
 * Colour: brand accent (coral, client-themed) carries the client's own usage
 * data; status greens are the FIXED semantic tokens; a plan allowance wires
 * into the hero the moment one exists (sheet E4).
 */
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  CalendarDays,
  CheckCircle2,
  Coins,
  Download,
  FileSpreadsheet,
  FileText,
  Loader2,
  Presentation,
  XCircle,
} from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { UsageModelsDonut } from '@/components/usage/UsageModelsDonut';
import { UsagePiecesTable } from '@/components/usage/UsagePiecesTable';
import { UsageQualityDistribution } from '@/components/usage/UsageQualityDistribution';
import { getUsageCredits } from '@/services/usageCreditsService';
import { getUsagePieces } from '@/services/usagePiecesService';
import { exportReport, type ReportFormat } from '@/services/usageReportExport';
import { useTenantStore } from '@/stores/useTenantStore';

function shortDate(iso: string): string {
  return new Date(`${iso}T00:00:00`).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
}

type ChartView = 'area' | 'bars' | 'cumulative';

const CHART_VIEWS: { key: ChartView; label: string }[] = [
  { key: 'area', label: 'Area' },
  { key: 'bars', label: 'Bars' },
  { key: 'cumulative', label: 'Cumulative' },
];

export default function UsageCreditsPage() {
  const currentTenant = useTenantStore((state) => state.currentTenant);
  const [chartView, setChartView] = useState<ChartView>('area');
  const [exporting, setExporting] = useState<ReportFormat | null>(null);
  // undefined = current month; otherwise a "YYYY-MM" past cycle.
  const [cycle, setCycle] = useState<string | undefined>(undefined);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['usage-credits', currentTenant, cycle ?? 'current'],
    queryFn: () => getUsageCredits(currentTenant as string, cycle),
    enabled: Boolean(currentTenant),
    staleTime: 60_000,
    placeholderData: (prev) => prev,
  });

  const { data: piecesData } = useQuery({
    queryKey: ['usage-pieces', currentTenant, cycle ?? 'current'],
    queryFn: () => getUsagePieces(currentTenant as string, cycle),
    enabled: Boolean(currentTenant),
    staleTime: 60_000,
    placeholderData: (prev) => prev,
  });
  const pieces = piecesData?.pieces ?? [];

  async function handleExport(format: ReportFormat) {
    if (!data) return;
    setExporting(format);
    try {
      await exportReport(format, data, pieces);
      toast.success('Report downloaded');
    } catch (err) {
      console.error('usage report export failed', err);
      toast.error("Couldn't build that file. Try again.");
    } finally {
      setExporting(null);
    }
  }

  const chartData = useMemo(() => {
    let runCompleted = 0;
    let runFailed = 0;
    return (data?.daily ?? []).map((d) => {
      runCompleted += d.completed;
      runFailed += d.failed;
      return {
        ...d,
        label: shortDate(d.date),
        cumulativeCompleted: runCompleted,
        cumulativeFailed: runFailed,
      };
    });
  }, [data]);

  return (
    <div className="w-full space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 font-display text-xl font-semibold">
            <Coins className="h-5 w-5 text-coral" />
            Usage &amp; credits
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            The content credits your workspace has used this cycle.
          </p>
        </div>
        {data ? (
          <div className="flex flex-wrap items-center gap-3">
            {/* Defensive: a backend not yet redeployed with these fields returns
                them undefined — the page must still render, never hard-crash. */}
            {(data.available_cycles?.length ?? 0) > 1 ? (
              <Select value={data.cycle || undefined} onValueChange={(v) => setCycle(v)}>
                <SelectTrigger className="h-8 w-[150px] text-xs">
                  <SelectValue placeholder="This cycle" />
                </SelectTrigger>
                <SelectContent>
                  {(data.available_cycles ?? []).map((c) => (
                    <SelectItem key={c.value} value={c.value} className="text-xs">
                      {c.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : null}
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <CalendarDays className="h-3.5 w-3.5" />
              {shortDate(data.period_start)} → {shortDate(data.period_end)}
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" disabled={exporting !== null}>
                  {exporting !== null ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Download className="h-4 w-4" />
                  )}
                  Export
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuLabel>Download report</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => handleExport('pdf')}>
                  <FileText className="h-4 w-4 text-coral" />
                  PDF document
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleExport('xlsx')}>
                  <FileSpreadsheet className="h-4 w-4 text-pace-good-dark" />
                  Excel workbook
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleExport('pptx')}>
                  <Presentation className="h-4 w-4 text-teal-dark" />
                  PowerPoint
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleExport('md')}>
                  <FileText className="h-4 w-4 text-muted-foreground" />
                  Markdown
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        ) : null}
      </div>

      {!currentTenant ? (
        <Card>
          <CardContent className="p-6 text-sm text-muted-foreground">
            Select a workspace to see its usage.
          </CardContent>
        </Card>
      ) : isLoading ? (
        <Card>
          <CardContent className="flex items-center gap-2 p-6 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading usage…
          </CardContent>
        </Card>
      ) : isError || !data ? (
        <Card>
          <CardContent className="p-6 text-sm text-muted-foreground">
            Usage isn't available right now. Try again shortly.
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Hero + stat cluster, filling the width */}
          <div className="grid gap-4 lg:grid-cols-[1.1fr_1fr]">
            <Card className="border-coral/30 bg-coral-light/20">
              <CardContent className="flex flex-col justify-between gap-3 p-5">
                <div className="text-xs font-medium uppercase tracking-wide text-coral-dark">
                  Credits used this cycle
                </div>
                <div className="font-display text-5xl font-semibold tabular-nums text-foreground">
                  {data.credits_used.toLocaleString()}
                </div>
                <p className="text-sm text-muted-foreground">
                  Your plan allowance and top-up options appear here once your
                  plan is set — nothing is capped yet.
                </p>
              </CardContent>
            </Card>

            <div className="grid grid-cols-2 gap-4">
              <StatTile
                icon={<CheckCircle2 className="h-4 w-4 text-pace-good-dark" />}
                label="Completed"
                value={data.completed_count}
                sub="pieces delivered"
              />
              <StatTile
                icon={<XCircle className="h-4 w-4 text-muted-foreground" />}
                label="Not completed"
                value={data.failed_count}
                sub="runs that didn't finish"
              />
              <StatTile
                icon={<FileText className="h-4 w-4 text-teal-dark" />}
                label="Avg / piece"
                value={
                  data.completed_count > 0
                    ? Math.round(data.credits_used / data.completed_count)
                    : 0
                }
                sub="credits per completed piece"
              />
              <StatTile
                icon={<Coins className="h-4 w-4 text-coral" />}
                label="Total pieces"
                value={data.completed_count + data.failed_count}
                sub="all generation runs"
              />
            </div>
          </div>

          {/* Credits over time — switchable view */}
          <Card>
            <CardContent className="p-5">
              <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                <div className="text-sm font-medium">
                  {chartView === 'cumulative' ? 'Credits so far this cycle' : 'Credits per day'}
                </div>
                <div className="flex items-center gap-1 rounded-md border border-border p-0.5 text-xs">
                  {CHART_VIEWS.map((v) => (
                    <button
                      key={v.key}
                      type="button"
                      onClick={() => setChartView(v.key)}
                      className={
                        chartView === v.key
                          ? 'rounded bg-coral-light px-2.5 py-1 font-medium text-coral-dark'
                          : 'rounded px-2.5 py-1 text-muted-foreground hover:text-foreground'
                      }
                    >
                      {v.label}
                    </button>
                  ))}
                </div>
              </div>
              {chartData.some((d) => d.credits > 0) ? (
                <CreditsChart view={chartView} chartData={chartData} />
              ) : (
                <div className="flex h-24 items-center justify-center text-sm text-muted-foreground">
                  No usage yet this cycle.
                </div>
              )}
            </CardContent>
          </Card>

          {/* Breakdown charts: models used + quality distribution */}
          <div className="grid gap-4 lg:grid-cols-2">
            <Card>
              <CardContent className="p-5">
                <div className="mb-3 text-sm font-medium">Models used</div>
                <UsageModelsDonut pieces={pieces} />
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-5">
                <div className="mb-3 text-sm font-medium">Quality distribution</div>
                <UsageQualityDistribution pieces={pieces} />
              </CardContent>
            </Card>
          </div>

          {/* Pieces table — sortable / resizable / filterable, rows link to the post */}
          <Card>
            <CardContent className="p-5">
              <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                <div className="text-sm font-medium">Pieces</div>
                <div className="text-xs text-muted-foreground">
                  {pieces.length} post{pieces.length === 1 ? '' : 's'} this cycle
                  {piecesData?.truncated ? ' (showing the most recent 500)' : ''} · click a row to
                  open it
                </div>
              </div>
              <UsagePiecesTable pieces={pieces} creditDollarValue={data.credit_dollar_value} />
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}

type ChartRow = {
  date: string;
  credits: number;
  completed: number;
  failed: number;
  label: string;
  cumulativeCompleted: number;
  cumulativeFailed: number;
};

// Two-series status split. Completed is the pace-good green already used on the
// Completed stat tile; "Not completed" is amber (pace-warn) — visible but not
// an alarming red, matching how the page downplays failures elsewhere. Both are
// FIXED semantic tokens, never client-themed. Stacked, they sum to the day's
// total credits.
const SERIES = [
  { key: 'completed', cumKey: 'cumulativeCompleted', name: 'Completed', color: 'var(--color-pace-good)', fill: 'url(#fillCompleted)' },
  { key: 'failed', cumKey: 'cumulativeFailed', name: 'Not completed', color: 'var(--color-pace-warn)', fill: 'url(#fillFailed)' },
] as const;

/**
 * The credits-over-time plot, in three interchangeable views (Area / Bars /
 * Cumulative). Two stacked series — Completed vs Not completed — so the legend
 * carries real meaning and a client sees the split at a glance. Recessive
 * grid/axes; status colors, never the client-themed brand hue.
 */
function CreditsChart({ view, chartData }: { view: ChartView; chartData: ChartRow[] }) {
  const isCumulative = view === 'cumulative';
  const mutedTick = { fontSize: 11, fill: 'var(--color-muted-foreground)' };
  const axisLabelStyle = {
    fontSize: 11,
    fill: 'var(--color-muted-foreground)',
    textAnchor: 'middle' as const,
  };
  const chartMargin = { top: 8, right: 16, bottom: 8, left: 4 };

  // NB: recharts finds its axes/legend via React.Children traversal, which does
  // NOT descend into a Fragment — wrapping these in <>…</> makes them silently
  // vanish (areas still render, so it looks "styled but unlabeled"). They must
  // be a flat ARRAY (arrays get flattened) or inline children. Keep as an array.
  const axes = [
    <CartesianGrid key="grid" vertical={false} stroke="var(--color-border)" strokeOpacity={0.5} />,
    <XAxis
      key="x"
      dataKey="label"
      tick={mutedTick}
      tickLine={{ stroke: 'var(--color-border)' }}
      axisLine={{ stroke: 'var(--color-border)' }}
      interval="preserveStartEnd"
      minTickGap={28}
      height={40}
      label={{ value: 'Date', position: 'insideBottom', offset: -2, style: axisLabelStyle }}
    />,
    <YAxis
      key="y"
      tick={mutedTick}
      tickLine={false}
      axisLine={false}
      width={58}
      allowDecimals={false}
      label={{ value: 'Credits', angle: -90, position: 'insideLeft', offset: 12, style: axisLabelStyle }}
    />,
    <Tooltip
      key="tt"
      cursor={
        view === 'bars'
          ? { fill: 'var(--color-muted-foreground)', fillOpacity: 0.08 }
          : { stroke: 'var(--color-muted-foreground)', strokeOpacity: 0.3 }
      }
      contentStyle={{ borderRadius: 8, border: '1px solid var(--color-border)', fontSize: 12 }}
      formatter={(value: number, name) => [`${value.toLocaleString()} credits`, name]}
      labelFormatter={(label) => label}
    />,
    <Legend
      key="lg"
      verticalAlign="top"
      align="left"
      height={22}
      iconType="circle"
      iconSize={9}
      formatter={(value) => (
        <span style={{ fontSize: 12, color: 'var(--color-muted-foreground)' }}>{value}</span>
      )}
    />,
  ];

  return (
    <div className="h-64 w-full">
      <ResponsiveContainer width="100%" height="100%">
        {view === 'bars' ? (
          <BarChart data={chartData} margin={chartMargin}>
            {axes}
            {SERIES.map((s, i) => (
              <Bar
                key={s.key}
                dataKey={s.key}
                name={s.name}
                stackId="c"
                fill={s.color}
                maxBarSize={22}
                // Round only the top series so the stack reads as one bar.
                radius={i === SERIES.length - 1 ? [3, 3, 0, 0] : undefined}
              />
            ))}
          </BarChart>
        ) : (
          <AreaChart data={chartData} margin={chartMargin}>
            <defs>
              <linearGradient id="fillCompleted" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="var(--color-pace-good)" stopOpacity={0.35} />
                <stop offset="100%" stopColor="var(--color-pace-good)" stopOpacity={0.03} />
              </linearGradient>
              <linearGradient id="fillFailed" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="var(--color-pace-warn)" stopOpacity={0.35} />
                <stop offset="100%" stopColor="var(--color-pace-warn)" stopOpacity={0.03} />
              </linearGradient>
            </defs>
            {axes}
            {SERIES.map((s) => (
              <Area
                key={s.key}
                type="monotone"
                dataKey={isCumulative ? s.cumKey : s.key}
                name={s.name}
                stackId="c"
                stroke={s.color}
                strokeWidth={2}
                fill={s.fill}
                dot={false}
              />
            ))}
          </AreaChart>
        )}
      </ResponsiveContainer>
    </div>
  );
}

function StatTile({
  icon,
  label,
  value,
  sub,
}: {
  icon: JSX.Element;
  label: string;
  value: number;
  sub: string;
}) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          {icon}
          {label}
        </div>
        <div className="mt-1 font-display text-2xl font-semibold tabular-nums text-foreground">
          {value.toLocaleString()}
        </div>
        <div className="text-xs text-muted-foreground">{sub}</div>
      </CardContent>
    </Card>
  );
}

